import { createIcpAgent } from "./icAgent.js";
import { encodeExtTokenIdentifier } from "./extToken.js";
import { principalToAccountId } from "./icpAccountId.js";
import { icpTokenIdThumbnailUrl, resolveIcpTokenImageUrl, } from "./icpImageResolver.js";
import { icpPortfolioNftKey } from "./tokenId.js";
import { resolveCollectionDisplayName } from "./collectionNames.js";
function extErrMessage(err) {
    if ("Other" in err)
        return err.Other;
    if ("InvalidToken" in err)
        return err.InvalidToken;
    return "";
}
async function createExtActor(canisterId, agent) {
    const { Actor } = await import("@dfinity/agent");
    const { IDL } = await import("@dfinity/candid");
    const resolvedAgent = agent ?? (await createIcpAgent());
    const CommonError = IDL.Variant({
        InvalidToken: IDL.Text,
        Other: IDL.Text,
    });
    // Entrepot / Toniq EXT Listing — empty Record({}) fails when any token is listed.
    const Listing = IDL.Record({
        locked: IDL.Opt(IDL.Int),
        seller: IDL.Principal,
        price: IDL.Nat64,
    });
    const idlFactory = () => IDL.Service({
        tokens_ext: IDL.Func([IDL.Text], [
            IDL.Variant({
                ok: IDL.Vec(IDL.Tuple(IDL.Nat32, IDL.Opt(Listing), IDL.Opt(IDL.Vec(IDL.Nat8)))),
                err: CommonError,
            }),
        ], ["query"]),
        tokens: IDL.Func([IDL.Text], [IDL.Variant({ ok: IDL.Vec(IDL.Nat32), err: CommonError })], ["query"]),
    });
    return Actor.createActor(idlFactory, {
        agent: resolvedAgent,
        canisterId,
    });
}
async function tryLoadIndices(actor, key) {
    try {
        const result = await actor.tokens_ext(key);
        if ("ok" in result)
            return result.ok.map(([index]) => Number(index));
        if (extErrMessage(result.err).toLowerCase() === "no tokens")
            return [];
    }
    catch {
        /* try legacy / next key */
    }
    try {
        const legacy = await actor.tokens(key);
        if ("ok" in legacy)
            return legacy.ok.map(Number);
    }
    catch {
        /* unsupported */
    }
    return null;
}
async function loadExtTokenIndices(actor, accountId, ownerPrincipal) {
    const byAccount = await tryLoadIndices(actor, accountId);
    if (byAccount && byAccount.length > 0)
        return byAccount;
    if (ownerPrincipal && ownerPrincipal !== accountId) {
        const byPrincipal = await tryLoadIndices(actor, ownerPrincipal);
        if (byPrincipal && byPrincipal.length > 0)
            return byPrincipal;
    }
    return byAccount ?? [];
}
/**
 * On-chain EXT v1/v2 portfolio scan for a single legacy collection canister.
 * tokenId is the decimal index (Migrator / OISY convention) — never the encoded tid.
 */
export async function fetchExtPortfolioForCanister(canisterId, ownerPrincipal, options) {
    if (!canisterId || !ownerPrincipal)
        return [];
    const accountId = await principalToAccountId(ownerPrincipal);
    const actor = await createExtActor(canisterId, options?.agent);
    const indices = await loadExtTokenIndices(actor, accountId, ownerPrincipal);
    const max = options?.maxTokens ?? 10_000;
    const capped = Number.isFinite(max) && max > 0 ? indices.slice(0, max) : indices;
    const out = [];
    for (const index of capped) {
        let imageUrl = resolveIcpTokenImageUrl(canisterId, index);
        try {
            const tid = await encodeExtTokenIdentifier(canisterId, index);
            imageUrl = icpTokenIdThumbnailUrl(canisterId, tid);
        }
        catch {
            /* keep index thumb */
        }
        const collectionName = resolveCollectionDisplayName(canisterId);
        out.push({
            chain: "icp",
            contractId: canisterId,
            tokenId: String(index),
            name: `${collectionName} #${index + 1}`,
            imageUrl,
            walletAddress: ownerPrincipal,
            collectionName,
        });
    }
    return out;
}
/** Scan multiple legacy EXT canisters and merge holdings (numeric tokenIds). */
export async function fetchExtPortfolioScan(ownerPrincipal, canisterIds, options) {
    const maxCanisters = options?.maxCanisters ?? 24;
    const concurrency = options?.concurrency ?? 4;
    const slice = canisterIds.slice(0, maxCanisters);
    const seen = new Set();
    const out = [];
    const signal = options?.signal;
    const agent = await createIcpAgent();
    let next = 0;
    let completed = 0;
    async function worker() {
        while (next < slice.length) {
            if (signal?.aborted)
                return;
            const i = next++;
            const canisterId = slice[i];
            let hitCount = 0;
            try {
                const batch = await fetchExtPortfolioForCanister(canisterId, ownerPrincipal, {
                    maxTokens: options?.maxTokensPerCanister ?? 10_000,
                    agent,
                });
                for (const nft of batch) {
                    const key = icpPortfolioNftKey(nft.contractId, nft.tokenId);
                    if (seen.has(key))
                        continue;
                    seen.add(key);
                    out.push(nft);
                    hitCount++;
                }
            }
            catch {
                /* skip unsupported canister */
            }
            completed++;
            options?.onCanisterDone?.({
                done: completed,
                total: slice.length,
                canisterId,
                hitCount,
            });
        }
    }
    await Promise.all(Array.from({ length: Math.min(concurrency, Math.max(slice.length, 1)) }, () => worker()));
    if (signal?.aborted) {
        const err = new Error("Aborted");
        err.name = "AbortError";
        throw err;
    }
    return out;
}
